/* eslint max-lines-per-function: ["error", {max: 230}] -- this overrides for the entire file */
/* eslint max-lines: ["error", {max: 285}] -- applies to entire file */
/* eslint max-statements: ["error", {max: 24}] -- applies to entire file */
import { useCallback, useEffect, useMemo } from "react";

import { formatRelative } from "date-fns";

import {
  AnalyticsFieldName,
  MessagesAnalyticsEvent,
  MessagesAnalyticsScreen,
} from "@/constants/analytics/Analytics";
import { TabSources } from "@/constants/analytics/Tabs";
import { EatingDisorderTreatmentArticleUrl } from "@/constants/GroupChannels";
import { ModalPath, ScreenPath, TabPath } from "@/constants/Navigation";
import { MessagesTestID } from "@/constants/TestID";
import { PatientTreatmentPhases } from "@/constants/User";
import { router, useFocusEffect } from "@/crossPlatform/expo-router";
import type { GroupChannel } from "@/crossPlatform/Sendbird";
import useMessageChannelSubDeepLink from "@/hooks/deepLinking/subDeepLinks/useMessageChannelSubDeepLink";
import useChatChannelContext from "@/hooks/useChatChannels";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import useHelpCenter from "@/hooks/useHelpCenter";
import { getUserTreatmentPhase } from "@/hooks/usePatientStatus";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppUserExternalId,
  selectAppUserIsPatient,
  selectAppUserIsSupport,
  useAppUserLinkedPatientsSelector,
} from "@/store/reducers/appUser";
import ChannelPreviewCard from "@/ui/components/messages/cards/ChannelPreviewCard";
import ChannelListFooter from "@/ui/components/messages/ChannelListFooter";
import ChannelListHeader from "@/ui/components/messages/ChannelListHeader";
import HelpCenterCard from "@/ui/components/profile/HelpCenterCard";
import { SectionHeader } from "@/ui/core";
import {
  Box,
  ScreenContainer,
  SectionList,
  WebContentConstraints,
} from "@/ui/layout";
import {
  getChannelDisplayName,
  isIndividualChannel,
} from "@/utils/groupChannel.util";
import {
  getProviderNames,
  sendbirdMemberToUserAvatar,
} from "@/utils/messages.util";
import { openUrl, toAbsolutePath } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getTabSelectorOptions } from "@/utils/user.util";

const Screen = () => {
  const appUserId = useAppSelector(selectAppUserExternalId);
  const isPatient = useAppSelector(selectAppUserIsPatient);
  const isSupport = useAppSelector(selectAppUserIsSupport);

  const { navigateWithEncoding } = useEncodedNavigation();

  const proxyPatients = useAppUserLinkedPatientsSelector();

  const {
    admissionsChannels,
    groupChannels,
    individualChannels,
    selectedPatientId,
    selectedPatientName,
    selectedPatientProfile,
    setSelectedPatientId,
  } = useChatChannelContext();

  const { track, trackPageView, trackUserSelectorEvent } = useAnalytics();
  const { captureException } = useAppMonitor();
  const { presentHelpCenter } = useHelpCenter();

  const selectedUserTreatmentPhase = getUserTreatmentPhase(
    selectedPatientProfile?.universalStatusCode,
  );
  const isSelectedUserInPreTreatmentPhase =
    selectedUserTreatmentPhase === PatientTreatmentPhases.PreTreatment;

  const handleSelectedChannel = ({
    channelDisplayName,
    channelUrl,
  }: {
    channelDisplayName: string;
    channelUrl: string;
  }) => {
    navigateWithEncoding({
      params: {
        channelDisplayName,
        channelUrl,
        userTreatmentPhase: selectedUserTreatmentPhase,
      },
      pathname: toAbsolutePath([TabPath.Messages, ScreenPath.GroupChannel]),
    });
  };

  useFocusEffect(
    useCallback(() => {
      trackPageView(MessagesAnalyticsScreen.ChannelList);
    }, [trackPageView]),
  );

  useEffect(() => {
    if (isPatient) return;
    if (!isNotNilNotEmpty(proxyPatients)) return;
    // only default to the first patient if nothing is selected yet
    if (isNotNilNotEmpty(selectedPatientId)) return;

    const firstPatientId = proxyPatients[0].externalId;
    setSelectedPatientId(firstPatientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedPatientId is recreated each render (context value isn't memoized), so including it would refire this effect on every provider re-render
  }, [isPatient, proxyPatients, selectedPatientId]);

  const tabSelectorOptions = getTabSelectorOptions({
    appUserId,
    isAppUserPatient: isPatient,
    patients: proxyPatients,
  });

  const sections: Section<GroupChannel>[] = useMemo(() => {
    const initialSections: Section<GroupChannel>[] = [];
    if (individualChannels.length > 0) {
      initialSections.push({
        data: individualChannels,
        title: "Direct messages",
      });
    }
    if (groupChannels.length > 0 || admissionsChannels.length > 0) {
      initialSections.push({
        data: [...groupChannels, ...admissionsChannels],
        title: "Group messages",
      });
    }

    return initialSections;
  }, [groupChannels, individualChannels, admissionsChannels]);

  const handleUserChange = (userId: string) => {
    setSelectedPatientId(userId);
    trackUserSelectorEvent({
      selectedUser: userId,
      source: MessagesAnalyticsScreen.ChannelList,
    });
  };
  const handleFooterPress = () => {
    track(MessagesAnalyticsEvent.AboutMessagesPressed, {
      [AnalyticsFieldName.Source]: MessagesAnalyticsScreen.ChannelList,
    });
    router.navigate({
      pathname: toAbsolutePath([TabPath.Messages, ModalPath.AboutMessages]),
    });
  };
  const handleLearnMorePress = () => {
    track(MessagesAnalyticsEvent.LearnMorePressed, {
      [AnalyticsFieldName.Source]: MessagesAnalyticsScreen.ChannelList,
    });
    void openUrl({
      onError: (error?: Error) => {
        captureException(new Error("Failed to open URL", { cause: error }));
      },
      scheme: "web",
      url: EatingDisorderTreatmentArticleUrl,
    });
  };
  const ListFooterComponent = (
    <ChannelListFooter
      onPress={
        isSelectedUserInPreTreatmentPhase
          ? handleLearnMorePress
          : handleFooterPress
      }
      shouldShowEmptyCard={isSelectedUserInPreTreatmentPhase}
      testID={MessagesTestID.ChannelList}
    />
  );
  const ListHeaderComponent = (
    <Box paddingBottom={12} paddingHorizontal={12}>
      <HelpCenterCard
        onPress={() =>
          void presentHelpCenter({
            intercomPresenterSource: TabSources.Messages,
          })
        }
      />
    </Box>
  );

  useMessageChannelSubDeepLink();

  return (
    <ScreenContainer>
      <Box style={WebContentConstraints}>
        <SectionHeader
          appUserId={appUserId}
          initialUserId={selectedPatientId}
          isAppUserSupport={isSupport}
          onUserChange={handleUserChange}
          testID={MessagesTestID.ChannelList}
          title="Messages"
          userTabs={tabSelectorOptions}
        />
      </Box>
      <SectionList
        keyExtractor={(item) => item.url}
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item: channel }) => {
          const channelDisplayName = getChannelDisplayName({
            channel,
            isAppUserPatient: isPatient,
            selectedPatientName,
          });
          const isGroupChannel = !isIndividualChannel(channel.url);

          // currently, native does not show avatars for individualChannel recipients;
          // gated to web to not impact native
          const individualChannelRecipient =
            isWeb() && !isGroupChannel
              ? channel.members.find((m) => m.userId !== appUserId)
              : undefined;
          const avatarUserId = individualChannelRecipient?.userId;

          const handleSelect = () => {
            handleSelectedChannel({
              channelDisplayName,
              channelUrl: channel.url,
            });
          };

          const { lastMessage, members, unreadMessageCount, url } = channel;

          const lastMessageRelativeTime = lastMessage?.createdAt
            ? formatRelative(lastMessage.createdAt, new Date())
            : undefined;

          return (
            <ChannelPreviewCard
              avatarUserId={avatarUserId}
              channelName={channelDisplayName}
              channelUrl={url}
              isGroupChannel={isGroupChannel}
              isLastMessageFileMessage={lastMessage?.isFileMessage()}
              key={url}
              lastMessage={lastMessage?.message}
              lastMessageTime={lastMessageRelativeTime}
              members={members.map(sendbirdMemberToUserAvatar)}
              onSelect={handleSelect}
              providerMembersDescription={getProviderNames(members)}
              unreadMessageCount={unreadMessageCount}
            />
          );
        }}
        renderSectionHeader={({ section }) => {
          if (isSelectedUserInPreTreatmentPhase) return null;
          return (
            <ChannelListHeader
              testID={MessagesTestID.ChannelList}
              title={section.title}
            />
          );
        }}
        sections={sections}
        separatorType="line"
        testID={MessagesTestID.ChannelList}
      />
    </ScreenContainer>
  );
};

export default Screen;
